import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { z } from 'zod';

import { showPromiseToast } from '@/lib/toast';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { useInitiateDeploymentMutation } from '@/store/api/deploymentApi';

import { zodResolver } from '@hookform/resolvers/zod';

const deploymentSchema = z
  .object({
    // Step 1: Database refresh
    force_database_refresh: z.boolean(),
    db_snapshot_source_instance: z.enum(['production', 'config']).optional(),

    // Step 2: Version selection
    version_category: z.enum(['production', 'snapshot']).optional(),

    // Step 3: ETL integrations (booleans)
    etl_application_enabled: z.boolean().optional(),
    etl_v2_application_enabled: z.boolean().optional(),

    // Step 3: Outbound integrations (will be converted to strings)
    ip_header_integration_enabled: z.boolean().optional(),
    award_header_integration_enabled: z.boolean().optional(),
    subaward_header_integration_enabled: z.boolean().optional(),
    subaward_invoice_integration_enabled: z.boolean().optional(),
  })
  .refine(
    (data) => {
      // If force_database_refresh is true, db_snapshot_source_instance is required
      if (data.force_database_refresh) {
        return !!data.db_snapshot_source_instance;
      }
      return true;
    },
    {
      message: 'Please select a snapshot source when refreshing the database',
      path: ['db_snapshot_source_instance'],
    },
  );

type DeploymentFormData = z.infer<typeof deploymentSchema>;

interface StartDeploymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: string;
  environment: string;
  currentConfig: Record<string, any> | null;
}

export const StartDeploymentModal = ({
  open,
  onOpenChange,
  client,
  environment,
  currentConfig,
}: StartDeploymentModalProps) => {
  const [initiateDeployment] = useInitiateDeploymentMutation();
  const [currentStep, setCurrentStep] = useState(1);

  // Extract current config values for defaults
  // Handles nested config structure: config.shared.version_category, config.etl.etl_application_enabled, etc.
  const getCurrentConfigValue = useCallback(
    (key: string, defaultValue: any = false) => {
      if (!currentConfig) return defaultValue;

      // Map keys to their nested paths in the config structure
      let value: any;

      // Version category is in shared section
      if (key === 'version_category') {
        value = currentConfig.shared?.version_category;
      }
      // ETL settings are in etl section
      else if (key === 'etl_application_enabled' || key === 'etl_v2_application_enabled') {
        value = currentConfig.etl?.[key];
      }
      // Outbound integration settings are in research.research_xml_config_props
      else if (key === 'ip_header_integration_enabled') {
        value =
          currentConfig.research?.research_xml_config_props?.[
            'ip.integration.to.external.award.enabled'
          ];
      } else if (key === 'award_header_integration_enabled') {
        value =
          currentConfig.research?.research_xml_config_props?.[
            'award.integration.to.external.financial.system.enabled'
          ];
      } else if (key === 'subaward_header_integration_enabled') {
        value =
          currentConfig.research?.research_xml_config_props?.[
            'subaward.header.integration.to.external.financial.system.enabled'
          ];
      } else if (key === 'subaward_invoice_integration_enabled') {
        value =
          currentConfig.research?.research_xml_config_props?.[
            'subaward.invoice.integration.to.external.financial.system.enabled'
          ];
      }
      // Fallback to direct lookup for any other keys
      else {
        value = currentConfig[key];
      }

      // Handle string "true"/"false" conversion to boolean
      if (value === 'true') return true;
      if (value === 'false') return false;
      return value ?? defaultValue;
    },
    [currentConfig],
  );

  const form = useForm<DeploymentFormData>({
    resolver: zodResolver(deploymentSchema),
    defaultValues: {
      force_database_refresh: false,
      db_snapshot_source_instance: undefined,
      version_category: getCurrentConfigValue('version_category', 'production'),
      etl_application_enabled: getCurrentConfigValue('etl_application_enabled', false),
      etl_v2_application_enabled: getCurrentConfigValue('etl_v2_application_enabled', false),
      ip_header_integration_enabled: getCurrentConfigValue('ip_header_integration_enabled', false),
      award_header_integration_enabled: getCurrentConfigValue(
        'award_header_integration_enabled',
        false,
      ),
      subaward_header_integration_enabled: getCurrentConfigValue(
        'subaward_header_integration_enabled',
        false,
      ),
      subaward_invoice_integration_enabled: getCurrentConfigValue(
        'subaward_invoice_integration_enabled',
        false,
      ),
    },
  });

  const forceRefresh = form.watch('force_database_refresh');

  // Reset form when modal opens or when client/environment changes
  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      form.reset({
        force_database_refresh: false,
        db_snapshot_source_instance: undefined,
        version_category: getCurrentConfigValue('version_category', 'production'),
        etl_application_enabled: getCurrentConfigValue('etl_application_enabled', false),
        etl_v2_application_enabled: getCurrentConfigValue('etl_v2_application_enabled', false),
        ip_header_integration_enabled: getCurrentConfigValue(
          'ip_header_integration_enabled',
          false,
        ),
        award_header_integration_enabled: getCurrentConfigValue(
          'award_header_integration_enabled',
          false,
        ),
        subaward_header_integration_enabled: getCurrentConfigValue(
          'subaward_header_integration_enabled',
          false,
        ),
        subaward_invoice_integration_enabled: getCurrentConfigValue(
          'subaward_invoice_integration_enabled',
          false,
        ),
      });
    }
  }, [open, client, environment, currentConfig, form, getCurrentConfigValue]);

  const onSubmit = async (data: DeploymentFormData) => {
    // Build the config payload
    const config: any = {
      force_database_refresh: data.force_database_refresh,
    };

    // Add optional fields only if they have values
    if (data.db_snapshot_source_instance) {
      config.db_snapshot_source_instance = data.db_snapshot_source_instance;
    }

    if (data.version_category) {
      config.version_category = data.version_category;
    }

    // ETL integrations (booleans)
    if (data.etl_application_enabled !== undefined) {
      config.etl_application_enabled = data.etl_application_enabled;
    }
    if (data.etl_v2_application_enabled !== undefined) {
      config.etl_v2_application_enabled = data.etl_v2_application_enabled;
    }

    // Outbound integrations (strings "true" or omit)
    if (data.ip_header_integration_enabled) {
      config.ip_header_integration_enabled = 'true';
    }
    if (data.award_header_integration_enabled) {
      config.award_header_integration_enabled = 'true';
    }
    if (data.subaward_header_integration_enabled) {
      config.subaward_header_integration_enabled = 'true';
    }
    if (data.subaward_invoice_integration_enabled) {
      config.subaward_invoice_integration_enabled = 'true';
    }

    await showPromiseToast(
      initiateDeployment({
        client_name: client,
        environment: environment,
        config: config,
      }).unwrap(),
      {
        loading: `Initiating deployment for ${client} ${environment}...`,
        success: () => `Deployment initiated successfully for ${client} ${environment}`,
        error: (error: any) =>
          error?.data?.detail || error?.message || 'Failed to initiate deployment',
      },
    );

    // Reset form and close modal
    form.reset();
    setCurrentStep(1);
    onOpenChange(false);
  };

  const handleNext = async () => {
    let fieldsToValidate: (keyof DeploymentFormData)[] = [];

    if (currentStep === 1) {
      fieldsToValidate = ['force_database_refresh', 'db_snapshot_source_instance'];
    } else if (currentStep === 2) {
      fieldsToValidate = ['version_category'];
    }

    const isValid = await form.trigger(fieldsToValidate);
    if (isValid) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const getCurrentState = (key: string): string => {
    const value = getCurrentConfigValue(key, false);
    return value ? 'Enabled' : 'Disabled';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Start Deployment</DialogTitle>
          <DialogDescription>
            Deploy to <span className='font-semibold'>{client}</span> •{' '}
            <span className='font-semibold'>{environment}</span>
          </DialogDescription>
          <div className='text-sm text-muted-foreground pt-2'>Step {currentStep} of 3</div>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            onKeyDown={(e) => {
              // Prevent Enter key from submitting form on steps 1 and 2
              if (e.key === 'Enter' && currentStep < 3) {
                e.preventDefault();
                handleNext();
              }
            }}
            className='space-y-6 py-4'
          >
            {/* Step 1: Database Refresh */}
            {currentStep === 1 && (
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Database Refresh</h3>

                <FormField
                  control={form.control}
                  name='force_database_refresh'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Would you like to wipe the database and refresh it?</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={(value) => field.onChange(value === 'true')}
                          value={field.value ? 'true' : 'false'}
                        >
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem value='true' id='refresh-yes' />
                            <Label htmlFor='refresh-yes' className='cursor-pointer'>
                              Yes
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem value='false' id='refresh-no' />
                            <Label htmlFor='refresh-no' className='cursor-pointer'>
                              No
                            </Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {forceRefresh && (
                  <FormField
                    control={form.control}
                    name='db_snapshot_source_instance'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Do you want the data refreshed from Production or Config?
                        </FormLabel>
                        <FormControl>
                          <RadioGroup onValueChange={field.onChange} value={field.value}>
                            <div className='flex items-center space-x-2'>
                              <RadioGroupItem value='production' id='source-production' />
                              <Label htmlFor='source-production' className='cursor-pointer'>
                                Production
                              </Label>
                            </div>
                            <div className='flex items-center space-x-2'>
                              <RadioGroupItem value='config' id='source-config' />
                              <Label htmlFor='source-config' className='cursor-pointer'>
                                Config
                              </Label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            )}

            {/* Step 2: Version Selection */}
            {currentStep === 2 && (
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Application Version</h3>

                <FormField
                  control={form.control}
                  name='version_category'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Which version of the application would you like to deploy?
                      </FormLabel>
                      <div className='text-sm text-muted-foreground mb-2'>
                        Current:{' '}
                        {(() => {
                          const currentValue = getCurrentConfigValue(
                            'version_category',
                            'production',
                          );
                          return currentValue === 'snapshot'
                            ? 'Development Build'
                            : 'Latest Production';
                        })()}
                      </div>
                      <FormControl>
                        <RadioGroup onValueChange={field.onChange} value={field.value}>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem value='production' id='version-production' />
                            <Label htmlFor='version-production' className='cursor-pointer'>
                              Latest Production
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem value='snapshot' id='version-snapshot' />
                            <Label htmlFor='version-snapshot' className='cursor-pointer'>
                              Development Build
                            </Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Step 3: Integration Options */}
            {currentStep === 3 && (
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Integration Options</h3>
                <p className='text-sm text-muted-foreground'>
                  Enable or disable integration features for this deployment
                </p>

                <div className='space-y-3'>
                  {/* ETL V1 */}
                  <FormField
                    control={form.control}
                    name='etl_application_enabled'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(value: boolean) => field.onChange(value)}
                          />
                        </FormControl>
                        <div className='space-y-1 leading-none'>
                          <FormLabel className='cursor-pointer'>
                            Enable ETL Version 1
                            <span className='text-muted-foreground text-sm ml-2'>
                              (Currently: {getCurrentState('etl_application_enabled')})
                            </span>
                          </FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* ETL V2 */}
                  <FormField
                    control={form.control}
                    name='etl_v2_application_enabled'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(value: boolean) => field.onChange(value)}
                          />
                        </FormControl>
                        <div className='space-y-1 leading-none'>
                          <FormLabel className='cursor-pointer'>
                            Enable ETL Version 2
                            <span className='text-muted-foreground text-sm ml-2'>
                              (Currently: {getCurrentState('etl_v2_application_enabled')})
                            </span>
                          </FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* Outbound Institutional Proposal */}
                  <FormField
                    control={form.control}
                    name='ip_header_integration_enabled'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(value: boolean) => field.onChange(value)}
                          />
                        </FormControl>
                        <div className='space-y-1 leading-none'>
                          <FormLabel className='cursor-pointer'>
                            Turn on Outbound Institutional Proposal Integration
                            <span className='text-muted-foreground text-sm ml-2'>
                              (Currently: {getCurrentState('ip_header_integration_enabled')})
                            </span>
                          </FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* Outbound Award */}
                  <FormField
                    control={form.control}
                    name='award_header_integration_enabled'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(value: boolean) => field.onChange(value)}
                          />
                        </FormControl>
                        <div className='space-y-1 leading-none'>
                          <FormLabel className='cursor-pointer'>
                            Turn on Outbound Award Integration
                            <span className='text-muted-foreground text-sm ml-2'>
                              (Currently: {getCurrentState('award_header_integration_enabled')})
                            </span>
                          </FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* Outbound Subaward */}
                  <FormField
                    control={form.control}
                    name='subaward_header_integration_enabled'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(value: boolean) => field.onChange(value)}
                          />
                        </FormControl>
                        <div className='space-y-1 leading-none'>
                          <FormLabel className='cursor-pointer'>
                            Turn on Outbound Subaward Integration
                            <span className='text-muted-foreground text-sm ml-2'>
                              (Currently: {getCurrentState('subaward_header_integration_enabled')})
                            </span>
                          </FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* Outbound Subaward Invoice */}
                  <FormField
                    control={form.control}
                    name='subaward_invoice_integration_enabled'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(value: boolean) => field.onChange(value)}
                          />
                        </FormControl>
                        <div className='space-y-1 leading-none'>
                          <FormLabel className='cursor-pointer'>
                            Turn on Outbound Subaward Invoice Integration
                            <span className='text-muted-foreground text-sm ml-2'>
                              (Currently: {getCurrentState('subaward_invoice_integration_enabled')})
                            </span>
                          </FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className='flex justify-between pt-4'>
              <div>
                {currentStep > 1 && (
                  <Button type='button' variant='outline' onClick={handleBack}>
                    Back
                  </Button>
                )}
              </div>
              <div className='flex gap-2'>
                {currentStep < 3 ? (
                  <Button
                    type='button'
                    onClick={(e) => {
                      e.preventDefault();
                      handleNext();
                    }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button type='submit' disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting ? 'Submitting...' : 'Submit Deployment'}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
